import format from "date-fns/format";
import { getAggregationPrefix } from "../../Pages/TableEditor/SettingsMenu/Layout/Column/RegularColumn";
import { absoluteDate } from "../../utils/dates/dateFunc";
import {
  getWeekNameByWeekRange,
  isDate,
} from "../../utils/formatters/dateFormatter";
import { allKeys, unique } from "../../utils/func";
import transformData from "./transformDataForRollupRows";
import { IO_UUID_FIELDS } from "./WriteBacksContext";
import formatter from "../../utils/formatters/formatter";

function getCheckedValues(filters, comparisonMode) {
  const { values } =
    filters
      .filter((f) => comparisonMode?.options?.includes(f.name))
      .find((f) => f.values.some((v) => v.checked)) ?? {};

  return values || [];
}

function getCheckedValueNames(values) {
  const names = values.filter((v) => v.checked);
  return [names[0]?.value, names[1]?.value];
}

function buildComparableHeaders(dynamicHighLevelKeys, values) {
  return dynamicHighLevelKeys.map((dk) =>
    dk.key
      ? { ...dk, key: dk.key === "[DYNAMIC_FILTER_1]" ? values[0] : values[1] }
      : dk
  );
}

export function setGroupingKeyMapping(key, weekOffset, groupingKeyMapping) {
  if (groupingKeyMapping === "[WEEK_RANGE]") {
    return getWeekNameByWeekRange(key, weekOffset);
  }

  const clearedKey = (key ?? "").replace(/[A-z]/g, "");
  if (groupingKeyMapping === "[MONTH_RANGE]" && isDate(clearedKey)) {
    return format(absoluteDate(clearedKey), "MM/dd/yyyy");
  }

  if (groupingKeyMapping === "[MONTH_YEAR]" && isDate(clearedKey)) {
    return format(absoluteDate(clearedKey), "MMMM yyyy");
  }

  if (groupingKeyMapping === "[MONTHLY]") {
    return formatter(key, "monthly");
  }

  if (groupingKeyMapping && isDate(key)) {
    return format(absoluteDate(clearedKey), groupingKeyMapping);
  }

  return key;
}

function removeNulls(row, groupingKey) {
  return groupingKey ? row[groupingKey] : true;
}

export default class {
  // columnKeys = null; // columnDefinition ne subTitles
  // headers = null; // display
  // sourceData = null;
  // data = null;
  // rows = null // to

  constructor(
    data,
    rollupOrSortedData,
    groupingKey,
    rowGroupKey,
    rowGroupKeys
  ) {
    this.sourceData = data;
    this.data = rollupOrSortedData;
    this.groupingKey = groupingKey;
    this.rowGroupKey = rowGroupKey;
    this.postfixes = [];
    this.rowGroupKeys = rowGroupKeys ?? [];
  }

  setHighLevelHeaders(staticHighLevelKeys) {
    this.highLevelHeaders = staticHighLevelKeys ?? null;
  }

  setDynamicHighLevelHeaders(dynamicHighLevelKeys, filters, comparisonMode) {
    const values = getCheckedValues(filters, comparisonMode);
    const names = getCheckedValueNames(values);
    if (names[0] && names[1]) {
      this.highLevelHeaders = buildComparableHeaders(
        dynamicHighLevelKeys,
        names
      );
    }
  }

  setHeaders(
    valueKeyAsSubTitle,
    rowGroupKey,
    staticGroupingKeys,
    prefix = "",
    groupingKey,
    groupingKeyMapping
  ) {
    const mainHeader = valueKeyAsSubTitle
      ? []
      : [rowGroupKey, ...this.rowGroupKeys];
    const hasHeaders = mainHeader.concat(
      staticGroupingKeys ||
        unique(
          this.data
            .filter((row) => removeNulls(row, groupingKey))
            .map(matchKeysWithPrefix)
        )
    );

    function matchKeysWithPrefix(d) {
      if (groupingKeyMapping) {
        return (
          prefix +
          setGroupingKeyMapping(
            d[groupingKey],
            d["WeekOffset"],
            groupingKeyMapping
          )
        );
      }

      return prefix + d[groupingKey];
    }

    function includeParentHeaders() {
      return (groupingKey || staticGroupingKeys) && hasHeaders;
    }

    this.headers = includeParentHeaders();
  }

  setSubTitles(
    subTitles,
    dynamicSubTitleKeys,
    groupingKeyMapping,
    dynamicPostfixColumn,
    overrides,
    rawRows
  ) {
    const self = this;

    if (dynamicSubTitleKeys) {
      const values = (this.rows[0]?.values || []).filter((val) =>
        removeNulls(val, self.groupingKey)
      );

      if (dynamicPostfixColumn) {
        this.postfixes = [
          "",
          ...values.map((row) => row[dynamicPostfixColumn]),
        ];
      }

      this.columnKeys = buildDynamicSubTitles(values);
      this.headers = buildDynamicNonUniqueHeaders(values, groupingKeyMapping);
    } else {
      this.columnKeys = subTitles?.length ? subTitles : nestInArray(this.data);
    }

    function nestInArray(data) {
      // for rawRows without subTitles we using overrides order
      // but we also have aggregation and we need add aggregation prefix to override name
      if (rawRows && overrides) {
        return [
          overrides.map((override) => {
            const { aggregation, ops, name } = override;
            const currentAggregation = ops?.aggregation || aggregation;
            const prefix = getAggregationPrefix(currentAggregation) ?? "";
            return prefix + name;
          }),
        ];
      }

      return allKeys(data)
        .filter((column) => !IO_UUID_FIELDS.includes(column))
        .map((k) => [k]);
    }

    function buildDynamicSubTitles(values) {
      const quantity = values.length + self.rowGroupKeys.length;

      return [
        [self.rowGroupKey],
        ...[...Array(quantity).keys()].map(() => dynamicSubTitleKeys),
      ];
    }

    function buildDynamicNonUniqueHeaders(values, groupingKeyMapping) {
      return [(self.headers ?? ["--"])[0], ...self.rowGroupKeys].concat(
        values.map((val) =>
          setGroupingKeyMapping(val[self.groupingKey], null, groupingKeyMapping)
        )
      );
    }
  }

  setRollupData(rollup, data) {
    this.data = transformData(rollup, data);
  }

  setRollupSubTitles(rollup) {
    const { dynamicColumnKey, matchForRowLabelKey } = rollup;
    const rollupSubtitlesArray = uniqueColumnValues(
      this.sourceData,
      dynamicColumnKey
    );
    this.columnKeys = rollupSubtitlesArray.reduce(
      (acc, curr) => [...acc, [curr]],
      [[matchForRowLabelKey]]
    );
  }

  setRows(rows) {
    this.rows = rows.map((row) => ({
      ...row,
      uniqueRowUuid: row.values[0]?.uniqueRowUuid,
    }));
  }

  setProgressBarsTotals(totals) {
    this.progressBarTotals = totals;
  }
}

function uniqueColumnValues(data, key) {
  return unique(data.map((d) => d[key]));
}

export const removeExcessRows = (array, config, groupingKey) => {
  if (!config) {
    return array;
  }

  const { column, value, operator } = config;

  const res = array.reduce((acc, row) => {
    if (operator === "<" && row[column] < value) {
      acc.push({ [groupingKey]: row[groupingKey], WeekOffset: row[column] });
    } else {
      acc.push(row);
    }

    return acc;
  }, []);

  return res;
};
